/**
 * The cookie consent banner.
 *
 * ── What this actually does ─────────────────────────────────────────────────
 * Nothing about blocking. Shopify's own systems do the blocking, and they were
 * already doing it before this file existed: with `Settings → Customer privacy`
 * carrying a region list, a visitor from a listed region has their analytics and
 * marketing data collection held back until a consent record exists. Web pixels
 * (app and custom alike) are gated the same way by Shopify's pixel manager, and
 * Google Consent Mode v2 reads the same record.
 *
 * So this file is the ASKING half only. It writes the record Shopify reads, via
 * the documented Customer Privacy API — the same call Shopify's own banner
 * makes. The merchant turns that banner off in admin, KEEPS the region list,
 * and this one takes over the conversation. Nothing here intercepts, patches or
 * shadows a Shopify behaviour.
 *
 *   https://shopify.dev/docs/api/customer-privacy
 *
 * ── Fail open, always ───────────────────────────────────────────────────────
 * 🔴 Every failure path ends in "show nothing and return". If the API will not
 * load, if the visitor is outside the configured regions, if scripts are blocked
 * outright — the store still works and the visitor still gets the conservative
 * outcome, because no consent record means Shopify keeps tracking switched off.
 * The dangerous direction would be a bug that RECORDS consent nobody gave, which
 * is why `set()` is reachable only from a click on a button the visitor pressed.
 * Nothing here writes consent on a timer, on scroll, or on load.
 *
 * ── 🔴 It cannot be tested on `shopify theme dev` ───────────────────────────
 * `setTrackingConsent` POSTs to Shopify's own consent endpoint, and from
 * `http://localhost:9292` that request fails cross-origin — the callback comes
 * back with `{error: "Error while setting storefront API consent: Failed to
 * fetch"}` and nothing is written. The UI is fine, the write is not, and the
 * failure is silent unless you read the callback argument.
 *
 * Test on the PREVIEW URL instead — `https://<shop>.myshopify.com/?preview_
 * theme_id=<id>`, which the dev server prints on startup. Same code, same
 * theme, real origin, and the write lands. Verified 2026-08-27: consent read
 * back as `{marketing: 'no', analytics: 'yes', …}` and `marketingAllowed()`
 * flipped to false.
 *
 * ── Why `shouldShowBanner()` is not the whole test ──────────────────────────
 * It answers "is this visitor in a region the merchant configured", and that is
 * all it answers — it stays true after the visitor has decided. Asking somebody
 * the same question on every page load is the commonest way a correct
 * integration still feels broken, so the second half of the test is
 * `currentVisitorConsent()`: any category holding 'yes' or 'no' means they have
 * already answered, and the banner stays down.
 */
if (!customElements.get('airflux-consent')) {
  customElements.define(
    'airflux-consent',
    class AirfluxConsent extends HTMLElement {
      connectedCallback() {
        this.banner = this.querySelector('[data-consent-banner]');
        this.card = this.querySelector('[data-consent-card]');
        this.reveal = this.querySelector('[data-consent-reveal]');
        this.toggle = this.querySelector('[data-consent-action="toggle"]');
        this.toggleLabel = this.querySelector('[data-consent-label]');
        this.toggles = [...this.querySelectorAll('[data-consent-category]')];

        this.labels = {
          collapsed: this.toggleLabel ? this.toggleLabel.textContent.trim() : '',
          expanded: this.dataset.labelHide || '',
        };

        /* The reopen link lives in the footer, on the other side of the
           document, and a policy page may grow one later. Delegating on
           `document` rather than binding a node means any element carrying the
           attribute works, on any template, without this file knowing where
           that markup is. It opens the card ALREADY expanded — somebody who
           went looking for cookie settings has asked for the options, not for
           the question again. */
        /* 🔴 `this.api` FIRST, and this guard is the whole reason the reopen
           link is not a lie.

           Reported from Chrome incognito 2026-08-28 and it reproduces wherever
           `loadFeatures` cannot load the consent API — an extension, tracking
           protection, a blocked request. `boot()` gives up quietly in that
           case, which is right: with no API there is no record, and with no
           record Shopify holds all tracking back. But THIS handler did not
           know that. It opened the card regardless, `reflect()` painted every
           switch off because it had nothing to read, and `set()` threw into a
           swallowed catch — so the visitor toggled, pressed Save, watched the
           card slide away and had changed precisely nothing. Four symptoms,
           one cause, and the worst kind: a privacy control that appears to
           work.

           Nothing to configure is the honest state here, so the triggers are
           hidden in `boot()` and this stays as the second lock. */
        this.onDocumentClick = (event) => {
          const trigger = event.target.closest('[data-af-consent-open]');
          if (!trigger || !this.api) return;
          event.preventDefault();
          this.showBanner();
          this.expand();
        };
        document.addEventListener('click', this.onDocumentClick);

        this.addEventListener('click', (event) => {
          const action = event.target.closest('[data-consent-action]');
          if (action) this.act(action.dataset.consentAction);
        });

        /* Escape collapses the options; it never dismisses the banner. Closing
           the question without answering it is not a decision the keyboard
           should be able to make by accident, and the card is not modal, so
           Escape has no other job here. */
        this.onKeydown = (event) => {
          if (event.key !== 'Escape' || !this.expanded) return;
          this.collapse();
          this.toggle?.focus();
        };
        this.addEventListener('keydown', this.onKeydown);

        /* 🔴 While the options are out, a finger on the card moves nothing but
           the options (2026-09-04).

           The card floats over the page and is not a scroll container, so on
           iOS a swipe on its title, copy or buttons panned the page underneath
           — and once the page was moving, the next swipe on the list went to
           the page too, the gesture already being its. CSS cannot stop this
           there: `touch-action: none` on the card is honoured by Chromium and
           ignored by WebKit for the document's own scrolling (measured in the
           iOS Simulator, `pinch-zoom` and `none` alike). So the touch is
           cancelled here instead, which is what the browser's own scroll
           listens for. Inside the list the browser scrolls as normal — unless
           the list is already at the end the finger is pushing towards, where
           the move is cancelled as well, so the end of the list never hands the
           gesture on to the page. Non-passive on purpose: a passive listener
           cannot cancel anything. */
        this.list = this.querySelector('.af-consent__list');
        this.onTouchStart = (event) => {
          this.touchY = event.touches[0]?.clientY ?? null;
        };
        this.onTouchMove = (event) => {
          if (!this.expanded || !event.cancelable) return;
          const inList = this.list && event.target.closest('.af-consent__list');
          if (!inList) {
            event.preventDefault();
            return;
          }
          const y = event.touches[0]?.clientY ?? null;
          if (this.touchY === null || y === null) return;
          const dy = y - this.touchY;
          const atTop = this.list.scrollTop <= 0;
          const atBottom = this.list.scrollTop + this.list.clientHeight >= this.list.scrollHeight - 1;
          if ((dy > 0 && atTop) || (dy < 0 && atBottom)) event.preventDefault();
        };
        this.card?.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.card?.addEventListener('touchmove', this.onTouchMove, { passive: false });

        this.boot();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        this.card?.removeEventListener('touchstart', this.onTouchStart);
        this.card?.removeEventListener('touchmove', this.onTouchMove);
      }

      /* ── Loading the API ───────────────────────────────────────────────────
         `loadFeatures` is required on the online store — the API is not on the
         page until something asks for it. Its callback takes an ERROR, so a
         falsy argument is success; that reads backwards from most callbacks and
         is the documented shape. */
      boot() {
        if (!window.Shopify?.loadFeatures) return this.retireTriggers();

        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          if (error || !window.Shopify.customerPrivacy) return this.retireTriggers();

          this.api = window.Shopify.customerPrivacy;
          this.reflect();
          this.publish();

          if (this.shouldAsk()) this.showBanner();
        });
      }

      /**
       * No API, no control — so take the control away rather than leave a
       * button that does nothing.
       *
       * 🔴 This is the visible half of the `this.api` guard on the reopen
       * handler. Leaving the footer link in place while the API is blocked
       * gives somebody a "Cookie settings" button that opens a card, accepts
       * their choices and saves none of them, which is worse than having no
       * button at all — it is a privacy control that lies about working.
       *
       * ⚠️ Nothing is lost by hiding it. Without the API there is no consent
       * record, and with no record Shopify holds analytics and marketing back
       * on its own — the visitor is already in the most private state the shop
       * can put them in. What the theme itself remembers stays session-only for
       * the same reason: `may()` reads a marker that is never written.
       */
      retireTriggers() {
        for (const el of document.querySelectorAll('[data-af-consent-open]')) el.hidden = true;
      }

      /**
       * Hands Shopify's answer to `window.AirfluxStorage`, which is what the
       * rest of the theme actually consults.
       *
       * 🔴 This is the only bridge between the consent record and what the
       * theme remembers, and it runs on EVERY page load, not only when the
       * banner is answered. That is what makes withdrawal take effect: a
       * visitor who turns Preferences off has the persistent copies deleted on
       * their next page, wherever they turned it off.
       *
       * The `…Allowed()` methods are used rather than raw `currentVisitorConsent`
       * values because they are region-aware. Outside the regions configured in
       * `Settings → Customer privacy` Shopify answers true with an empty record
       * — which is correct, and means a visitor somewhere PECR does not reach
       * keeps their car the way they always did.
       */
      publish() {
        if (!window.AirfluxStorage) return;

        try {
          const analytics = this.api.analyticsProcessingAllowed();

          window.AirfluxStorage.setConsent({
            preferences: this.api.preferencesProcessingAllowed(),
            analytics,
          });

          /* 🔴 Announced, and that is not decoration — it is what stops the
             shop paying for a lookup it is then not allowed to learn from.

             This file is deferred and the API behind it is a second async load,
             so the answer arrives LATE. Everything that ran first — including
             the reg lookup, which is the one thing on the page that costs real
             money — saw "analytics: no" simply because nobody had been asked
             yet. `airflux-fitment.js` therefore parks a report it is not yet
             allowed to send, and this event is what releases it.

             `decided` is the whole distinction: `analytics === false` alone
             cannot tell "not asked yet" from "asked and said no", and the two
             deserve opposite treatment — park, or discard. */
          document.dispatchEvent(
            new CustomEvent('airflux:consent-changed', {
              detail: { analytics, decided: this.hasAnswered() },
              bubbles: true,
            })
          );
        } catch (e) {
          /* Leave the marker alone rather than guessing. An unreadable API is
             not evidence that consent was withdrawn, and deleting somebody's
             stored car on the strength of a thrown error is the one outcome
             worse than keeping it a moment too long. */
        }
      }

      /**
       * Has this visitor answered the question yet?
       *
       * Separate from `shouldAsk` because two different things need it: the
       * banner, to stay down, and `publish`, to tell "not asked yet" apart from
       * "asked and refused" for anything holding work back pending an answer.
       *
       * An empty string means "never asked". Anything else — 'yes' or 'no' — is
       * a decision already on record for that category.
       *
       * 🔴 ONLY the three categories this banner actually asks about.
       * `sale_of_data` is deliberately NOT in this list, and leaving it in was a
       * real bug: Shopify populates that field ITSELF, without any visitor
       * interaction. Measured 2026-08-27 on a fresh origin — no GPC signal,
       * `saleOfDataRegion()` false — the record came back as `{analytics: '',
       * marketing: '', preferences: '', sale_of_data: 'no'}`. Counting that as
       * an answer suppressed the banner for a visitor who had never been asked
       * anything, which is the one failure this file must not have. Evidence
       * that the QUESTION was answered can only come from the categories the
       * question covers.
       *
       * Unreadable reads as NOT answered, which errs towards asking again
       * rather than towards assuming a consent that may not exist.
       */
      hasAnswered() {
        try {
          const consent = this.api?.currentVisitorConsent() || {};
          return ['analytics', 'marketing', 'preferences'].some(
            (key) => consent[key] === 'yes' || consent[key] === 'no'
          );
        } catch (e) {
          return false;
        }
      }

      shouldAsk() {
        if (!this.api) return false;

        try {
          if (!this.api.shouldShowBanner()) return false;
          return !this.hasAnswered();
        } catch (e) {
          return false;
        }
      }

      /* Paint the switches from whatever is already recorded, so somebody
         reopening the options sees their own choices rather than the defaults.
         Unset categories stay off: a pre-ticked box is not consent. */
      reflect() {
        let consent = {};

        try {
          consent = this.api?.currentVisitorConsent() || {};
        } catch (e) {
          /* Leave every switch off. */
        }

        this.toggles.forEach((input) => {
          input.checked = consent[input.dataset.consentCategory] === 'yes';
        });
      }

      act(action) {
        if (action === 'toggle') return this.expanded ? this.collapse() : this.expand();

        if (action === 'accept') return this.set({ analytics: true, marketing: true, preferences: true });
        if (action === 'reject') return this.set({ analytics: false, marketing: false, preferences: false });

        if (action === 'save') {
          const chosen = {};
          this.toggles.forEach((input) => {
            chosen[input.dataset.consentCategory] = input.checked;
          });
          return this.set(chosen);
        }
      }

      /**
       * The one call that writes anything.
       *
       * ⚠️ `sale_of_data` rides with `marketing`. It governs sharing with third
       * parties for behavioural advertising, which is the same bucket a shopper
       * means by the marketing switch, and Shopify applies it only in regions
       * configured for a sale-of-data opt-out — the UK is not one, so today it
       * is inert here and correct if the region list ever grows. The GPC browser
       * signal is honoured by Shopify independently and cannot be set from this
       * call, which is why there is no code for it.
       */
      set(choice) {
        const payload = { ...choice, sale_of_data: choice.marketing === true };

        this.collapse();
        this.hideBanner();

        try {
          /* `publish` re-reads the live API rather than trusting `payload`, so
             a write that silently failed leaves the theme's memory matching
             whatever Shopify actually holds instead of what we asked for. */
          this.api.setTrackingConsent(payload, () => {
            this.reflect();
            this.publish();
          });
        } catch (e) {
          /* The record is Shopify's to keep. If the call fails the visitor is
             left with no consent recorded, which is the safe side of the line,
             so there is nothing to retry and nothing to tell them about. */
        }
      }

      /* ── The two heights ───────────────────────────────────────────────────
         🔴 A disclosure, not a dialog. Focus is never moved INTO the region on
         open — the visitor is already where they meant to be, and stealing
         focus is what makes a non-modal panel behave like a modal one. `inert`
         is what keeps the closed region out of the tab order and the
         accessibility tree. The CSS now also sets the closed reveal to
         `display: none` (the panel snaps to size and its options fade in, since
         2026-09-04); `inert` stays as the explicit statement of the same fact,
         and for any stylesheet that does not load. */
      expand() {
        if (!this.card) return;
        this.reflect();
        this.expanded = true;
        this.card.setAttribute('data-expanded', '');
        this.reveal?.removeAttribute('inert');
        this.toggle?.setAttribute('aria-expanded', 'true');
        if (this.toggleLabel && this.labels.expanded) this.toggleLabel.textContent = this.labels.expanded;
      }

      collapse() {
        if (!this.card) return;
        this.expanded = false;
        this.card.removeAttribute('data-expanded');
        this.reveal?.setAttribute('inert', '');
        this.toggle?.setAttribute('aria-expanded', 'false');
        if (this.toggleLabel && this.labels.collapsed) this.toggleLabel.textContent = this.labels.collapsed;
      }

      /* `hidden` is the no-JS guard and is dropped for good on first reveal;
         after that `data-shown` alone carries the state, so the card can
         transition out as well as in. */
      showBanner() {
        if (!this.banner) return;
        this.banner.hidden = false;
        /* Suppresses the product page's sticky Add-to-basket bar, which is fixed
           to the same corner of the same screen. It returns the moment the
           banner goes — see `body[data-af-consent]` in `airflux-theme.css`. */
        document.body.setAttribute('data-af-consent', '');
        window.requestAnimationFrame(() => this.banner.setAttribute('data-shown', ''));
      }

      hideBanner() {
        this.banner?.removeAttribute('data-shown');
        document.body.removeAttribute('data-af-consent');
      }
    }
  );
}
